import mixpanel from 'mixpanel-browser';
import useAuthStore from 'stores/authStore';

// Initialize Mixpanel with your project token
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || '';

// Event Names Configuration
export const EVENT_NAMES = {
  // Authentication Events
  USER_SIGNED_IN: 'User Signed In',           // Alias for login
  USER_LOGGED_IN: 'User Logged In',           // Login event
  USER_SIGNED_UP: 'User Signed Up',           // Registration event
  USER_REGISTERED: 'User Registered',         // Alias for signup
  USER_SIGNED_OUT: 'User Signed Out',         // Alias for logout
  USER_LOGGED_OUT: 'User Logged Out',         // Logout event

  // Classification Events
  CLASSIFICATION_STARTED: 'Classification Started',
  CLASSIFICATION_IN_PROGRESS: 'Classification In Progress',
  CLASSIFICATION_COMPLETED: 'Classification Completed',
  CLASSIFICATION_SUCCEEDED: 'Classification Succeeded', 
  CLASSIFICATION_ERROR: 'Classification Error',
  CLASSIFICATION_FAILED: 'Classification Failed',

  // Verification Events
  VERIFICATION_STARTED: 'Verification Started',
  VERIFICATION_COMPLETED: 'Verification Completed',

  // Chat Events
  MESSAGE_SENT: 'Message Sent',
  NEW_CONVERSATION: 'New Conversation Started',
  CONVERSATION_OPENED: 'Conversation Opened',
  CONVERSATION_DELETED: 'Conversation Deleted',

  // Feedback Events
  FEEDBACK_SUBMITTED: 'Feedback Submitted',

  // Reclassification Events
  RECLASSIFICATION_REQUESTED: 'Reclassification Requested',
  RECLASSIFICATION_COMPLETED: 'Reclassification Completed',
  RECLASSIFICATION_FAILED: 'Reclassification Failed',

  // File Upload Events
  FILE_UPLOAD_STARTED: 'File Upload Started',
  FILE_UPLOAD_COMPLETED: 'File Upload Completed',
  FILE_UPLOAD_FAILED: 'File Upload Failed',
};

class AnalyticsService {
  private isInitialized = false;

  init() {
    if (MIXPANEL_TOKEN && !this.isInitialized) {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: process.env.NODE_ENV === 'development',
        track_pageview: false,      // Disabled: stops [Auto] Page View events
        autocapture: false,          // Disabled: stops [Auto] Element Click events
        persistence: 'localStorage',
        record_sessions_percent: 100,
      });
      this.isInitialized = true;
      console.log('Mixpanel initialized successfully - autocapture disabled, custom events only');

      // Identify user immediately if already logged in
      // This ensures events tracked on app load are associated with the user
      const authState = useAuthStore.getState();
      const user = authState.getUser();

      if (user && user.userId) {
        this.identify(user.userId, {
          email: user.email,
          name: user.name,
          givenName: user.givenName,
          familyName: user.familyName,
          verifiedEmail: user.verifiedEmail,
          loginIds: user.loginIds,
        });
        console.log('✅ Mixpanel: User auto-identified on init', {
          userId: user.userId,
          email: user.email,
          name: user.name,
        });
      } else {
        console.log('⚠️ Mixpanel: No user logged in on init - events will be anonymous until login');
      }

      // Log identity status for debugging
      const identityInfo = this.getIdentityInfo();
      console.log(' Mixpanel Identity Status:', identityInfo);
    } else if (!MIXPANEL_TOKEN) {
      console.warn('Mixpanel token not found. Analytics disabled.');
    }
  }

  // Identify user
  identify(userId: string, userProperties?: Record<string, any>) {
    if (!this.isInitialized) {
      console.warn('Mixpanel: Cannot identify user - not initialized');
      return;
    }

    // Identify the user in Mixpanel
    mixpanel.identify(userId);

    // Set user properties in Mixpanel People
    if (userProperties) {
      mixpanel.people.set(userProperties);
    }

    // Also register super properties so ALL future events include user context
    mixpanel.register({
      userId: userId,
      ...userProperties,
    });

    console.log('✅ Mixpanel: User identified and properties set', {
      userId,
      propertiesCount: userProperties ? Object.keys(userProperties).length : 0,
    });
  }

  // Reset user on logout
  reset() {
    if (!this.isInitialized) return;

    // Unregister super properties
    mixpanel.unregister('userId');
    mixpanel.unregister('email');
    mixpanel.unregister('name');

    // Reset Mixpanel (clears distinct_id and starts new session)
    mixpanel.reset();

    console.log('✅ Mixpanel: User session reset - future events will be anonymous');
  }

  // Track generic event
  track(eventName: string, properties?: Record<string, any>) {
    if (!this.isInitialized) {
      console.warn('Mixpanel: Cannot track event - not initialized', eventName);
      return;
    }

    mixpanel.track(eventName, properties);

    if (process.env.NODE_ENV === 'development') {
      console.log('Mixpanel Event:', eventName, properties);
    }
  }

  // Get current Mixpanel distinct_id
  getDistinctId(): string | undefined {
    if (!this.isInitialized) return undefined;
    return mixpanel.get_distinct_id();
  }

  // Check if user is identified (returns distinct_id)
  getIdentityInfo() {
    if (!this.isInitialized) {
      return { isInitialized: false, distinctId: null, isIdentified: false };
    }

    const distinctId = mixpanel.get_distinct_id();
    // Mixpanel auto-generates UUIDs for anonymous users
    const isAutoGenerated = distinctId && distinctId.length > 30 && distinctId.includes('-');

    return {
      isInitialized: true,
      distinctId,
      isIdentified: !isAutoGenerated,
      isAnonymous: isAutoGenerated,
    };
  }

  // Manual page view tracking
  trackPageView(pageName: string, additionalProperties?: Record<string, any>) {
    if (!this.isInitialized) return;
    mixpanel.track('Page Viewed', {
      page: pageName,
      ...additionalProperties,
      timestamp: new Date().toISOString(),
    });
  }

  // Generic button click tracking
  trackButtonClick(buttonName: string, location: string, additionalProperties?: Record<string, any>) {
    if (!this.isInitialized) return;
    mixpanel.track('Button Clicked', {
      button: buttonName,
      location,
      ...additionalProperties,
      timestamp: new Date().toISOString(),
    });
  }

  // Authentication Events
  trackLogin(method: 'email' | 'google', userId: string) {
    const eventData = {
      method,
      userId,
      timestamp: new Date().toISOString(),
    };

    // Track both "Logged In" and "Signed In"
    this.track(EVENT_NAMES.USER_LOGGED_IN, eventData);
    this.track(EVENT_NAMES.USER_SIGNED_IN, eventData);
  }

  trackSignup(method: 'email' | 'google', userId: string) {
    const eventData = {
      method,
      userId,
      timestamp: new Date().toISOString(),
    };

    // Track both "Signed Up" and "Registered" for flexibility
    this.track(EVENT_NAMES.USER_SIGNED_UP, eventData);
    this.track(EVENT_NAMES.USER_REGISTERED, eventData);
  }

  trackLogout(userId: string) {
    const eventData = {
      userId,
      timestamp: new Date().toISOString(),
    };

    // Track both "Logged Out" and "Signed Out" for flexibility
    this.track(EVENT_NAMES.USER_LOGGED_OUT, eventData);
    this.track(EVENT_NAMES.USER_SIGNED_OUT, eventData);
  }

  // Classification Events
  trackClassificationStarted(data: {
    type: 'text' | 'file' | 'both';
    hasFile: boolean;
    fileType?: string;
    fileSize?: number;
    conversationId?: string;
  }) {
    this.track(EVENT_NAMES.CLASSIFICATION_STARTED, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  trackClassificationInProgress(data: {
    jobId?: string;
    conversationId?: string;
    progress?: number;
    status?: string;
  }) {
    this.track(EVENT_NAMES.CLASSIFICATION_IN_PROGRESS, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  trackClassificationCompleted(data: {
    jobId?: string;
    conversationId?: string;
    duration?: number;
    hasHTSCode: boolean;
    confidence?: number;
  }) {
    const eventData = {
      ...data,
      timestamp: new Date().toISOString(),
    };

    // Track "Completed"
    this.track(EVENT_NAMES.CLASSIFICATION_COMPLETED, eventData);

    // If it has an HTS code, also track as "Succeeded"
    if (data.hasHTSCode) {
      this.track(EVENT_NAMES.CLASSIFICATION_SUCCEEDED, {
        ...eventData,
        success: true,
      });
    }
  }

  trackClassificationError(data: {
    error: string;
    jobId?: string;
    conversationId?: string;
  }) {
    const eventData = {
      ...data,
      timestamp: new Date().toISOString(),
    };

    // Track both "Error" and "Failed"
    this.track(EVENT_NAMES.CLASSIFICATION_ERROR, eventData);
    this.track(EVENT_NAMES.CLASSIFICATION_FAILED, eventData);
  }

  // Verification Events
  trackVerificationStarted(sessionId: string) {
    this.track(EVENT_NAMES.VERIFICATION_STARTED, {
      sessionId,
      timestamp: new Date().toISOString(),
    });
  }

  trackVerificationCompleted(sessionId: string, responseType: string) {
    this.track(EVENT_NAMES.VERIFICATION_COMPLETED, {
      sessionId,
      responseType,
      timestamp: new Date().toISOString(),
    });
  }

  // Chat Events
  trackMessageSent(data: {
    messageType: 'text' | 'file' | 'both';
    conversationId?: string;
    hasAttachment: boolean;
  }) {
    this.track(EVENT_NAMES.MESSAGE_SENT, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  trackNewConversation() {
    this.track(EVENT_NAMES.NEW_CONVERSATION, {
      timestamp: new Date().toISOString(),
    });
  }

  trackConversationOpened(conversationId: string) {
    this.track(EVENT_NAMES.CONVERSATION_OPENED, {
      conversationId,
      timestamp: new Date().toISOString(),
    });
  }

  trackConversationDeleted(conversationId: string) {
    this.track(EVENT_NAMES.CONVERSATION_DELETED, {
      conversationId,
      timestamp: new Date().toISOString(),
    });
  }

  // Feedback Events
  trackFeedback(data: {
    jobId: string;
    rating: 'positive' | 'negative';
    comment?: string;
  }) {
    this.track(EVENT_NAMES.FEEDBACK_SUBMITTED, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  // Reclassification Events
  trackReclassificationRequested(data: {
    documentId: string;
    jobId: string;
    userMessage: string;
  }) {
    this.track(EVENT_NAMES.RECLASSIFICATION_REQUESTED, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  trackReclassificationCompleted(data: {
    documentId: string;
    jobId: string;
  }) {
    this.track(EVENT_NAMES.RECLASSIFICATION_COMPLETED, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  trackReclassificationFailed(data: {
    documentId: string;
    jobId?: string;
    error: string;
  }) {
    this.track(EVENT_NAMES.RECLASSIFICATION_FAILED, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  // File Upload Events
  trackFileUploadStarted(data: {
    fileType: string;
    fileSize: number;
    fileName: string;
  }) {
    this.track(EVENT_NAMES.FILE_UPLOAD_STARTED, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  trackFileUploadCompleted(data: {
    fileType: string;
    fileSize: number;
    duration: number;
  }) {
    this.track(EVENT_NAMES.FILE_UPLOAD_COMPLETED, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }

  trackFileUploadFailed(data: {
    fileType: string;
    fileSize: number;
    error: string;
  }) {
    this.track(EVENT_NAMES.FILE_UPLOAD_FAILED, {
      ...data,
      timestamp: new Date().toISOString(),
    });
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
